import { z } from "zod";
import { documentExtractionSignedUrl, pdfDocument } from "../models/extraction";

export type PdfDocumentExtraction = z.infer<typeof pdfDocument>;

/**
 * Tells the API this bundle can read a presigned URL in `extractionUrlInfo`.
 *
 * The API serves the presigned shape only to a request carrying this header, so
 * a bundle built before `resolveDocumentExtraction` existed — including one
 * still loaded in a tab across a deploy — keeps receiving the inline elements it
 * knows how to parse. Sent on every request by `fetchFastApi`: the capability
 * belongs to the bundle, not to a particular call, and declaring it per call
 * site would silently drop back to the inline payload on any future document
 * request that forgot it.
 *
 * Removing this header without also removing the handling below re-opens that
 * gap; removing the handling without the header serves this bundle a shape it
 * cannot parse.
 */
export const PRESIGNED_EXTRACTION_CAPABILITY_HEADER =
  "X-Midship-Client-Supports-Presigned-Extraction";

/**
 * Resolve the `extractionUrlInfo` field of a document response into the parsed
 * PDF extraction.
 *
 * The API serves it two ways. Preferred: a presigned URL to a presentation copy
 * of the elements in blob storage, which is fetched here. Fallback, for
 * documents parsed before that copy existed: the elements inline. Both carry the
 * same camelCase `pdfDocument` shape once loaded.
 */
export async function resolveDocumentExtraction(
  info: unknown,
): Promise<PdfDocumentExtraction> {
  const signed = documentExtractionSignedUrl.safeParse(info);
  if (!signed.success) {
    return pdfDocument.parse(info);
  }

  const response = await fetch(signed.data.url);
  if (!response.ok) {
    throw new Error(`Failed to fetch document extraction (${response.status})`);
  }
  return pdfDocument.parse(await response.json());
}
